import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    ChevronLeft, ChevronRight, X, Download, Loader2, Maximize2, Volume2, Pause,
} from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

/**
 * Full-screen in-app PDF reader with animated page turns.
 * Backend renders the PDF to base64 JPEGs (cached on the lesson doc).
 * Pages slide horizontally using framer-motion; keyboard ← → arrows work.
 */
export default function LessonPdfReader({ open, onOpenChange, lessonId, lessonTitle, downloadUrl, onReachEnd }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pages, setPages] = useState([]);
    const [fallbackUrl, setFallbackUrl] = useState(null);
    const [idx, setIdx] = useState(0);
    const [direction, setDirection] = useState(1); // 1 = next, -1 = prev
    const [audioLoading, setAudioLoading] = useState(false);
    const [audioPlaying, setAudioPlaying] = useState(false);
    const [audioError, setAudioError] = useState(null);
    const containerRef = useRef(null);
    const audioRef = useRef(null);

    // Stop audio on page change or close
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.src = "";
            audioRef.current = null;
        }
        setAudioPlaying(false);
        setAudioLoading(false);
        setAudioError(null);
    }, [idx, open]);

    // Auto-mark the lesson complete when the reader reaches the last page.
    // Parent (Learn.jsx) decides whether to actually fire the API call — we
    // just announce the event. Guard against premature firing while the
    // pages array is still loading (length 0) AND skip single-page PDFs so
    // a 1-page document doesn't auto-complete the moment it opens.
    useEffect(() => {
        if (!open) return;
        if (pages.length < 2) return;
        if (idx < pages.length - 1) return;
        onReachEnd?.();
    }, [open, idx, pages.length, onReachEnd]);

    const readThisPage = useCallback(async () => {
        if (audioRef.current && audioPlaying) {
            audioRef.current.pause();
            setAudioPlaying(false);
            return;
        }
        if (audioRef.current && !audioPlaying) {
            audioRef.current.play();
            setAudioPlaying(true);
            return;
        }
        setAudioLoading(true);
        setAudioError(null);
        try {
            const token = localStorage.getItem("token");
            const resp = await fetch(
                `${API}/lessons/${lessonId}/pdf-pages/${idx}/audio`,
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` },
                },
            );
            if (!resp.ok) {
                let msg = "Could not narrate this page.";
                try {
                    const err = await resp.json();
                    msg = err.detail || msg;
                } catch { /* noop */ }
                setAudioError(msg);
                toast.error(msg);
                return;
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            audio.onplay = () => setAudioPlaying(true);
            audio.onpause = () => setAudioPlaying(false);
            audio.onended = () => {
                setAudioPlaying(false);
                URL.revokeObjectURL(url);
            };
            audioRef.current = audio;
            audio.play();
        } catch (e) {
            const msg = e?.message || "Audio playback failed.";
            setAudioError(msg);
            toast.error(msg);
        } finally {
            setAudioLoading(false);
        }
    }, [lessonId, idx, audioPlaying]);

    // Reset & load
    useEffect(() => {
        if (!open || !lessonId) return;
        setLoading(true);
        setError(null);
        setIdx(0);
        setDirection(1);
        setPages([]);
        setFallbackUrl(null);
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/lessons/${lessonId}/pdf-pages`);
                if (cancelled) return;
                if (data.pages && data.pages.length > 0) {
                    setPages(data.pages);
                } else if (data.fallback_data_url || data.fallback_url) {
                    // Prefer the inlined data URL (bulletproof — no cross-origin,
                    // CSP, or ingress concerns). Fall back to same-origin URL.
                    setFallbackUrl(data.fallback_data_url || data.fallback_url);
                } else {
                    setError("This lesson has no readable PDF pages.");
                }
            } catch (e) {
                if (cancelled) return;
                // If the rendered-pages API fails AND we have a downloadUrl from
                // the parent, fall back to native iframe so the student is never stuck.
                if (downloadUrl) {
                    setFallbackUrl(downloadUrl);
                } else {
                    const msg = e?.response?.data?.detail || "Could not open the PDF.";
                    setError(msg);
                    toast.error(msg);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, lessonId, downloadUrl]);

    const goPrev = useCallback(() => {
        setIdx((i) => {
            if (i <= 0) return i;
            setDirection(-1);
            return i - 1;
        });
    }, []);
    const goNext = useCallback(() => {
        setIdx((i) => {
            if (i >= pages.length - 1) return i;
            setDirection(1);
            return i + 1;
        });
    }, [pages.length]);

    // Keyboard nav
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
            else if (e.key === "ArrowRight") { e.preventDefault(); goNext(); }
            else if (e.key === "Escape") { e.preventDefault(); onOpenChange(false); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, goPrev, goNext, onOpenChange]);

    if (!open) return null;

    const atStart = idx === 0;
    const atEnd = idx >= pages.length - 1;

    const slideVariants = {
        enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0, rotateY: dir > 0 ? 12 : -12 }),
        center: { x: 0, opacity: 1, rotateY: 0, transition: { duration: 0.35, ease: [0.4, 0, 0.2, 1] } },
        exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0, rotateY: dir > 0 ? -12 : 12, transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] } }),
    };

    return (
        <div
            className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-sm flex flex-col"
            data-testid="lesson-pdf-reader"
        >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card">
                <Maximize2 className="h-4 w-4 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">
                        {lessonTitle || "Lesson PDF"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {loading
                            ? "Loading…"
                            : fallbackUrl
                              ? "Native PDF view"
                              : `Page ${idx + 1} of ${pages.length}`}
                    </div>
                </div>
                {!loading && !error && pages.length > 0 ? (
                    <Button
                        variant={audioPlaying ? "default" : "outline"}
                        size="sm"
                        onClick={readThisPage}
                        disabled={audioLoading}
                        className="gap-1.5"
                        data-testid="pdf-reader-read-page"
                        title={audioPlaying ? "Pause" : "Read this page aloud"}
                    >
                        {audioLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : audioPlaying ? (
                            <Pause className="h-4 w-4" />
                        ) : (
                            <Volume2 className="h-4 w-4" />
                        )}
                        <span className="hidden sm:inline">
                            {audioLoading ? "Loading…" : audioPlaying ? "Pause" : "Read page"}
                        </span>
                    </Button>
                ) : null}
                {downloadUrl ? (
                    <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        data-testid="pdf-reader-download"
                    >
                        <a href={downloadUrl} target="_blank" rel="noreferrer">
                            <Download className="h-4 w-4" />
                            Download
                        </a>
                    </Button>
                ) : null}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onOpenChange(false)}
                    data-testid="pdf-reader-close"
                    aria-label="Close reader"
                >
                    <X className="h-5 w-5" />
                </Button>
            </div>

            {/* Page canvas */}
            <div
                ref={containerRef}
                className="flex-1 relative overflow-hidden flex items-center justify-center p-4 sm:p-8"
                style={{ perspective: "1600px" }}
            >
                {loading ? (
                    <div className="flex flex-col items-center gap-3 text-muted-foreground">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <div className="text-sm">Rendering the pages for reading&hellip;</div>
                        <div className="text-xs">First open takes a few seconds — it&rsquo;s instant after that.</div>
                    </div>
                ) : fallbackUrl ? (
                    <iframe
                        src={fallbackUrl}
                        title={lessonTitle || "Lesson PDF"}
                        className="w-full h-full rounded-md border border-border bg-card"
                        data-testid="pdf-reader-iframe"
                    />
                ) : error ? (
                    <div className="text-sm text-destructive max-w-md text-center" data-testid="pdf-reader-error">
                        {error}
                    </div>
                ) : pages.length === 0 ? (
                    <div className="text-sm text-muted-foreground">No pages to show.</div>
                ) : (
                    <>
                        {/* Prev button */}
                        <button
                            type="button"
                            onClick={goPrev}
                            disabled={atStart}
                            aria-label="Previous page"
                            data-testid="pdf-reader-prev"
                            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-card/90 hover:bg-card border border-border shadow-lg flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-all hover:scale-110"
                        >
                            <ChevronLeft className="h-6 w-6" />
                        </button>

                        {/* Pages (animated slide + subtle 3D rotate) */}
                        <AnimatePresence initial={false} custom={direction} mode="wait">
                            <motion.img
                                key={idx}
                                src={pages[idx]}
                                custom={direction}
                                variants={slideVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                alt={`Page ${idx + 1}`}
                                data-testid={`pdf-reader-page-${idx}`}
                                className="max-h-full max-w-[min(900px,100%)] rounded-md shadow-2xl bg-white"
                                style={{ transformStyle: "preserve-3d" }}
                                draggable={false}
                            />
                        </AnimatePresence>

                        {/* Next button */}
                        <button
                            type="button"
                            onClick={goNext}
                            disabled={atEnd}
                            aria-label="Next page"
                            data-testid="pdf-reader-next"
                            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-card/90 hover:bg-card border border-border shadow-lg flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-all hover:scale-110"
                        >
                            <ChevronRight className="h-6 w-6" />
                        </button>
                    </>
                )}
            </div>

            {/* Footer: page slider + keyboard hint */}
            {!loading && !error && pages.length > 1 ? (
                <div className="px-6 py-3 border-t border-border bg-card flex items-center gap-4">
                    <input
                        type="range"
                        min={1}
                        max={pages.length}
                        value={idx + 1}
                        onChange={(e) => {
                            const next = Number(e.target.value) - 1;
                            setDirection(next > idx ? 1 : -1);
                            setIdx(next);
                        }}
                        className="flex-1 accent-primary cursor-pointer"
                        data-testid="pdf-reader-slider"
                        aria-label="Jump to page"
                    />
                    <div className="text-xs text-muted-foreground whitespace-nowrap hidden sm:block">
                        ← / → to flip · Esc to close
                    </div>
                </div>
            ) : null}
        </div>
    );
}
